import { CameraController } from './controls/CameraController';
import { Loader } from './utils/Loader';
import { Mouse } from './input/Mouse';
import { Keyboard } from './input/Keyboard';
import { CreatorAccessory } from '@/types/creator';
import { EventEmitter } from './utils/EventEmitter';
import { SerializedRenderer, ThreeEvents } from './types';
import { Exporter, ExportType } from './utils/Exporter';
import { LightController } from './controls/LightsController';
import { ProductTypeVariation } from '@/api/ProductTypeVariationApi';
import { RenderUtils } from './utils/RenderUtils';
import { AccessoriesManager } from './managers/AccessoriesManager';
import { CreatorAction } from '@/types/types';
import { ActionHandler } from './utils/ActionHandler';
import {
	WebGLRenderer,
	Group,
	Scene,
	Color,
	SRGBColorSpace,
	ACESFilmicToneMapping,
	EquirectangularReflectionMapping,
	Mesh,
	BufferGeometry,
} from 'three';
import { ProductManager } from './managers/ProductManager';
import { AssetsLib } from './utils/AssetsLib';
import { formatExactDate } from '@/utils/utilities';
import {
	acceleratedRaycast,
	computeBoundsTree,
	disposeBoundsTree,
} from 'three-mesh-bvh';
import RENDERER_CONFIG from '@/configs/rendererConfig';
import envMapPath from '@/assets/hdri/studio.hdr';
import Stats from 'three/examples/jsm/libs/stats.module.js';
import APP_CONFIG from '@/configs/appConfig';

const url = new URL(window.location.href);
const searchParams = new URLSearchParams(url.search);
const isDev = !!searchParams.get('DEV') || import.meta.env.DEV;

Mesh.prototype.raycast = acceleratedRaycast;
BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;

const stats = new Stats();
// if (isDev) document.body.appendChild(stats.dom);

export class ProductRenderer {
	private static instance: ProductRenderer = new ProductRenderer();
	private loader = Loader.getInstance();
	private exporter = new Exporter(this);
	private assetsLib = AssetsLib.getInstance();
	isTouchDevice = false;
	isDev = isDev;
	actionHandler = new ActionHandler(this);
	accessoriesManager = new AccessoriesManager(this);
	productManager = new ProductManager(this);
	renderer!: WebGLRenderer;
	screenshotRenderer!: WebGLRenderer;
	lightsController!: LightController;
	cameraController!: CameraController;
	mouse = new Mouse(this);
	keyboard = new Keyboard(this);
	emitter = new EventEmitter<ThreeEvents>();
	loading = false;
	product = new Group();
	scene!: Scene;
	canvas: HTMLCanvasElement | null = null;

	private constructor() {}

	static getInstance() {
		return ProductRenderer.instance;
	}

	private initRenderers() {
		this.renderer = new WebGLRenderer({
			canvas: this.canvas!,
			antialias: true,
		});

		this.scene.background = new Color(APP_CONFIG.DEFAULT_BACKGROUND);

		const offscreenCanvas = new OffscreenCanvas(
			RENDERER_CONFIG.SCREENSHOT.WIDTH,
			RENDERER_CONFIG.SCREENSHOT.HEIGHT
		);

		this.screenshotRenderer = new WebGLRenderer({
			canvas: offscreenCanvas,
			antialias: true,
			preserveDrawingBuffer: true,
		});

		for (const renderer of [this.renderer, this.screenshotRenderer]) {
			renderer.outputColorSpace = SRGBColorSpace;
			renderer.toneMapping = ACESFilmicToneMapping;
			renderer.toneMappingExposure = 0.8;
		}

		this.renderer.setAnimationLoop(() => {
			if (import.meta.env.DEV) stats.begin();
			this.cameraController.controls.update();
			this.renderer.render(this.scene, this.cameraController.camera);

			if (import.meta.env.DEV) stats.end();
		});
	}

	private loadEnvMap() {
		this.loader.HDRLoader.load(envMapPath, (texture) => {
			texture.mapping = EquirectangularReflectionMapping;
			texture.colorSpace = SRGBColorSpace;
			this.scene.environment = texture;
			this.scene.environmentIntensity = 0.6;
		});
	}

	init(canvas: HTMLCanvasElement, isTouchDevice: boolean) {
		this.canvas = canvas;
		this.isTouchDevice = isTouchDevice;

		this.scene = new Scene();

		this.scene.add(this.product);

		this.cameraController = new CameraController(this);
		this.lightsController = new LightController(this);

		this.mouse.init();
		this.keyboard.init();

		this.initRenderers();
		this.loadEnvMap();
		this.handleResize();
	}

	handleActions(creatorActions: CreatorAction[]) {
		for (const action of creatorActions) {
			this.actionHandler.dispatchAction(action);
		}
	}

	clear() {
		RenderUtils.clearGroup(this.product);
		this.accessoriesManager.clear();
	}

	async handleSelectVariation(
		variation: ProductTypeVariation,
		resetAccessories: boolean
	) {
		if (!variation.asset?.model) throw new Error('MISSING_MODEL');

		const model = await this.assetsLib.getModel(variation.asset.model.path);

		if (resetAccessories) this.clear();
		else this.productManager.clearModel();

		this.productManager.initProduct(model);
		this.cameraController.centerCamera();
	}

	async screenshot() {
		this.cameraController.prepareScreenshotCamera();
		this.screenshotRenderer.render(
			this.scene,
			this.cameraController.screenshotCamera
		);

		const canvas = this.screenshotRenderer
			.domElement as unknown as OffscreenCanvas;
		const blob = await canvas.convertToBlob();

		const file = new File(
			[blob],
			`Konfiguracja projektu ${formatExactDate()}.png`
		);

		return file;
	}

	handleCameraZoom(value: number) {
		this.cameraController.handleCameraZoom(value);
	}

	handleChangeBackground(background: string) {
		this.scene.background = new Color(background);
	}

	handleResize() {
		const { width, height } =
			this.canvas!.parentElement!.getBoundingClientRect();

		this.cameraController.handleResize(width / height);

		this.renderer.setSize(width, height);
	}

	async exportProjectModel(type: ExportType) {
		return await this.exporter.exportProject(type);
	}

	dispose() {
		this.renderer.dispose();
		this.cameraController.dispose();

		const ctx = this.canvas!.getContext('webgl2')!;
		ctx.clear(new Color(APP_CONFIG.DEFAULT_BACKGROUND).getHex());
	}

	export(): SerializedRenderer {
		return {
			objectsManager: this.accessoriesManager.export(),
		};
	}

	async load(
		data: SerializedRenderer,
		variation: ProductTypeVariation,
		creatorAccessories: CreatorAccessory[]
	) {
		await this.handleSelectVariation(variation, true);
		this.accessoriesManager.load(data.objectsManager, creatorAccessories);
	}
}
